/**
 * Art lessons: figure + construction plan → ordinary `Drawing` (steps of animatable strokes).
 *
 *   artDrawing('animals-cat', t('Sitting Cat', 'Mèo Con Ngồi'), meta, CAT, [
 *     { stage: 1, parts: ['head', 'ears'], caption: t('Draw a *circle* …', '…') },
 *     { stage: 1, caption: … },                // no parts = every remaining stage-1 guide
 *     { stage: 2, parts: ['head', 'ears'], caption: … },
 *     { stage: 3, caption: … },
 *     { stage: 4, caption: … },                // erase the guides
 *     { stage: 5, caption: … },                // colour & shade everything left
 *   ])
 *
 * A step may cover several stages (`stage: [4, 5]`) — quick doodles do that. Each element is
 * assigned to the FIRST step that covers its stage and lists its part (or lists no parts).
 */
import type { L } from '../../text';
import type { Difficulty, Drawing, DrawingStep, SkillId, Stroke, SubjectCategory } from '../model';
import { appearStage, type ArtFigure, type ArtStage } from './types';

export interface ArtStepDef {
  stage: ArtStage | ArtStage[];
  /** Part names (element `p`) drawn in this step, in drawing order. Omit = all remaining. */
  parts?: string[];
  /** Per-stage part lists (override `parts` for that stage) — e.g. remix recap: all guides, kept outlines. */
  partsByStage?: Partial<Record<ArtStage, string[]>>;
  caption: L;
  tip?: L;
}

/** Timing for strokes that are not drawn with the pencil. */
export const ART_MS = { spot: 260, fill: 240, erase: 1100 } as const;

export interface CompiledArt {
  steps: DrawingStep[];
  errors: string[];
}

const stagesOf = (s: ArtStage | ArtStage[]) => [...new Set(Array.isArray(s) ? s : [s])].sort();

export function compileArtSteps(fig: ArtFigure, defs: ArtStepDef[]): CompiledArt {
  const errors: string[] = [];
  const usedG = new Set<number>();
  const usedL = new Set<number>();
  const usedF = new Set<number>();
  let erased = false;
  let lastStage = 0;

  const pick = <T extends { p?: string }>(items: { el: T; i: number }[], parts?: string[]) => {
    if (!parts) return items;
    const out: { el: T; i: number }[] = [];
    for (const part of parts) for (const it of items) if ((it.el.p ?? '') === part && !out.includes(it)) out.push(it);
    return out;
  };

  const steps = defs.map((def, si): DrawingStep => {
    const stages = stagesOf(def.stage);
    if (stages[0]! < lastStage) errors.push(`step ${si + 1}: stage ${stages[0]} after stage ${lastStage} (construction order)`);
    lastStage = stages[stages.length - 1]!;
    const strokes: Stroke[] = [];
    for (const st of stages) {
      const parts = def.partsByStage?.[st] ?? def.parts;
      if (st === 1) {
        const free = fig.guides.map((el, i) => ({ el, i })).filter((x) => !usedG.has(x.i));
        for (const { el, i } of pick(free, parts)) {
          usedG.add(i);
          strokes.push({ d: el.d, tone: 'guide', ref: `g${i}` });
        }
      } else if (st === 2 || st === 3) {
        const free = fig.layers.map((el, i) => ({ el, i })).filter((x) => !usedL.has(x.i) && appearStage(x.el) === st);
        for (const { el, i } of pick(free, parts)) {
          usedL.add(i);
          if (el.k === 'region') strokes.push({ d: el.d, tone: 'line', ref: `l${i}` });
          else if (el.k === 'ink') strokes.push({ d: el.d, tone: 'detail', ref: `l${i}` });
          else strokes.push({ d: el.d, tone: 'accent', ref: `l${i}`, ms: ART_MS.spot });
        }
      } else if (st === 4) {
        if (!fig.guides.length) errors.push(`step ${si + 1}: erase stage but the figure has no guides`);
        else if (!erased) {
          erased = true;
          strokes.push({ d: fig.guides[0]!.d, tone: 'erase', ref: 'e', ms: ART_MS.erase });
        }
      } else {
        const regions = fig.layers.map((el, i) => ({ el, i })).filter((x) => x.el.k === 'region' && !usedF.has(x.i));
        for (const { i } of pick(regions, parts)) {
          usedF.add(i);
          strokes.push({ d: fig.layers[i]!.d, tone: 'fill', ref: `f${i}`, ms: ART_MS.fill });
        }
        const late = fig.layers.map((el, i) => ({ el, i })).filter((x) => !usedL.has(x.i) && appearStage(x.el) === 5);
        for (const { el, i } of pick(late, parts)) {
          usedL.add(i);
          strokes.push({ d: el.d, tone: 'fill', ref: `l${i}`, ms: ART_MS.spot });
        }
      }
    }
    if (!strokes.length) errors.push(`step ${si + 1}: picks nothing (parts ${def.parts?.join(', ') ?? '*'} at stage ${stages.join('+')})`);
    return { caption: def.caption, strokes, stage: stages, ...(def.tip ? { tip: def.tip } : {}) };
  });

  fig.guides.forEach((g, i) => {
    if (!usedG.has(i)) errors.push(`guide ${i}${g.p ? ` (${g.p})` : ''} is never drawn`);
  });
  fig.layers.forEach((l, i) => {
    if (!usedL.has(i)) errors.push(`layer ${i} ${l.k}${l.p ? ` (${l.p})` : ''} at stage ${appearStage(l)} is never drawn`);
    if (l.k === 'region' && !usedF.has(i)) errors.push(`region ${i}${l.p ? ` (${l.p})` : ''} is never coloured (add a stage-5 step)`);
  });
  if (fig.guides.length && !erased) errors.push('guides are never erased (add a stage-4 step)');
  return { steps, errors };
}

export interface ArtDrawingMeta {
  subject: string;
  category: SubjectCategory;
  skills: SkillId[];
  difficulty?: Difficulty;
  minutes?: number;
}

/** Plan errors per drawing id (reported by `validateContent`; never thrown, so one bad figure can't crash the app). */
export const ART_PLAN_ERRORS = new Map<string, string[]>();

/** Build a lesson drawing from a figure + construction plan. */
export function artDrawing(id: string, title: L, meta: ArtDrawingMeta, figure: ArtFigure, plan: ArtStepDef[]): Drawing {
  const { steps, errors } = compileArtSteps(figure, plan);
  if (errors.length) ART_PLAN_ERRORS.set(id, errors);
  else ART_PLAN_ERRORS.delete(id);
  return {
    id,
    title,
    subject: meta.subject,
    category: meta.category,
    viewBox: figure.vb,
    steps,
    skills: meta.skills,
    difficulty: meta.difficulty ?? 1,
    minutes: meta.minutes ?? Math.max(3, Math.round(steps.length * 1.3)),
    art: figure,
    artStatus: 'final',
  };
}

/* ----------------------------------- frames ----------------------------------- */

/**
 * What of the figure is visible: per-guide draw progress, guide erase progress, per-layer appear
 * progress (outline / ink / spot) and per-region colour progress, all 0..1.
 */
export interface ArtFrame {
  g: number[];
  erase: number;
  l: number[];
  f: number[];
}

/** Frame after steps `from..upTo` with optional per-step stroke progress (steps before `from` are complete). */
export function artFrame(d: Drawing, upTo = d.steps.length - 1, from = 0, progress?: number[][]): ArtFrame {
  const fig = d.art;
  const fr: ArtFrame = { g: new Array(fig?.guides.length ?? 0).fill(0), erase: 0, l: new Array(fig?.layers.length ?? 0).fill(0), f: new Array(fig?.layers.length ?? 0).fill(0) };
  const last = Math.min(upTo, d.steps.length - 1);
  for (let i = 0; i <= last; i++) {
    const prog = i >= from ? progress?.[i - from] : undefined;
    d.steps[i]!.strokes.forEach((s, j) => {
      const p = i < from || !progress ? 1 : (prog?.[j] ?? 0);
      const r = s.ref;
      if (!r) return;
      const n = Number(r.slice(1));
      if (r === 'e') fr.erase = p;
      else if (r[0] === 'g') fr.g[n] = p;
      else if (r[0] === 'l') fr.l[n] = p;
      else if (r[0] === 'f') fr.f[n] = p;
    });
  }
  return fr;
}

/** Frame of a whole figure at a construction stage (thumbnails, style board, mascots). */
export function stageFrame(fig: ArtFigure, stage: ArtStage): ArtFrame {
  return {
    g: fig.guides.map(() => 1),
    erase: stage >= 4 ? 1 : 0,
    l: fig.layers.map((l) => (stage >= 5 || appearStage(l) <= stage ? 1 : 0)),
    f: fig.layers.map(() => (stage >= 5 ? 1 : 0)),
  };
}

/** Indices of the step that colours the figure (for the shading on/off toggle). */
export const colourStepIndex = (d: Drawing) => d.steps.findIndex((s) => s.strokes.some((x) => x.tone === 'fill'));

/** Figure elements a step animates (`g3`, `l5`, …) — used to highlight the current step. */
export const stepRefs = (d: Drawing, step: number): string[] => (d.steps[step]?.strokes ?? []).flatMap((s) => (s.ref ? [s.ref] : []));
